import { Helmet } from "react-helmet-async";
import { Route, Routes, Navigate } from "react-router-dom";
import { Analytics, Loading } from "@ottatech/shared-components";
import styled from "styled-components";
import React, { lazy, Suspense, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { useMutation, useQuery } from "@apollo/client";

import { useCookiebot } from "utils/cookiebot";
import { PageWithNavBar } from "components/NavBar";
import { AccountSettings } from "pages/AccountSettings";
import { DeleteMyAccount } from "pages/DeleteMyAccount";
import { EmployeeSurvey } from "pages/EmployeeSurvey";
import { ForgotPassword } from "pages/ForgotPassword";
import Jobs from "pages/Jobs/JobsRoutes";
import { Login } from "pages/Login";
import { SignupPage as Signup } from "pages/Signup";
import { Logout } from "pages/Logout";
import { LoggedInRoute } from "utils/routing/LoggedInRoute";
import { PreferencesSetRoute } from "utils/routing/PreferencesSetRoute";
import { SignedOutRoute } from "utils/routing/SignedOutRoute";
import { OAuthCallbacks } from "pages/OAuthCallbacks";
import { analyticsClickListener } from "utils/analytics/analyticsClickListener";
import { Toast } from "utils/toast";
import { Callback } from "pages/Callbacks";
import { useMaintenance } from "providers/maintenance";
import { Maintenance } from "components/Maintenance";
import { PreferencesRoute } from "pages/Preferences";
import { ApplyModalWrapper } from "pages/Dashboard/ApplyModalWrapper";
import { CurrentUserDocument, UpdateLastActiveDocument } from "schema";
import { JobsRedirect } from "pages/Jobs/JobsRedirect";
import { OAuthAuthorize } from "pages/OAuthAuthorize";
import Onboarding from "pages/Onboarding";
import { ApplySignUp } from "pages/ApplySignUp";
import { ReferralRedirect } from "pages/Referrals/Redirect";
import { Ottacue } from "components/Ottacue";
import { Referral } from "pages/Referrals";

const Dashboard = lazy(() => import("pages/Dashboard"));
const Companies = lazy(() => import("pages/Companies"));
const Salaries = lazy(() => import("pages/Salaries"));
const Profile = lazy(() => import("pages/Profile"));
const Messaging = lazy(() => import("pages/Messaging"));
const InitialPreferences = lazy(
  () => import("pages/Preferences/InitialPreferences")
);
const JobPreferences = lazy(() => import("pages/Preferences/JobPreferences"));
const ShortlistJob = lazy(() => import("pages/Dashboard/ShortlistJob"));
const EmployeeSurveySignUpPage = lazy(
  () => import("pages/Companies/EmployeeSurveySignUpPage")
);
const EmployeeSurveyLandingPage = lazy(
  () => import("pages/Companies/EmployeeSurveyLandingPage")
);

// This components assures the page is at least the height of the browser window
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export function App(): React.ReactElement {
  const isInMaintenance = useMaintenance();
  const { data } = useQuery(CurrentUserDocument);
  const [updateLastActive] = useMutation(UpdateLastActiveDocument);
  useCookiebot();
  useEffect(() => {
    if (data?.currentUser?.id) {
      updateLastActive();
    }
  }, [data?.currentUser?.id, updateLastActive]);
  useIdleTimer({
    timeout: 1000 * 60 * 60 * 12,
    onIdle: () => window.location.reload(),
  });

  useEffect(() => {
    document.addEventListener("click", analyticsClickListener, {
      passive: true,
      capture: true,
    });
    return () => {
      document.removeEventListener("click", analyticsClickListener, {
        capture: true,
      });
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Otta - The better way to find a job in tech</title>
      </Helmet>
      {isInMaintenance ? (
        <Maintenance />
      ) : (
        <>
          <Analytics />
          <PageWrapper>
            <Routes>
              <Route path="/oauth2/authorize" element={<OAuthAuthorize />} />
              <Route
                path="join/:referrerId"
                element={
                  <SignedOutRoute>
                    <ReferralRedirect />
                  </SignedOutRoute>
                }
              />
              <Route
                path="/sign-up"
                element={
                  <SignedOutRoute>
                    <Signup />
                  </SignedOutRoute>
                }
              />
              <Route
                path="/companies/write-an-endorsement"
                element={<EmployeeSurveyLandingPage />}
              />

              <Route
                path="/companies/:companyUrlSafeName/write-an-endorsement"
                element={<EmployeeSurveySignUpPage />}
              />
              <Route path="/" element={<PageWithNavBar />}>
                <Route path="/apply/sign-up" element={<ApplySignUp />}>
                  <Route index element={<ApplySignUp />} />
                  <Route path=":jobExternalId" element={<ApplySignUp />} />
                </Route>
                <Route
                  path="/"
                  element={
                    <PreferencesSetRoute
                      element={
                        <Suspense fallback={<Loading />}>
                          <Dashboard />
                        </Suspense>
                      }
                    />
                  }
                >
                  <Route
                    path="dashboard/jobs/:jobId/apply"
                    element={<ApplyModalWrapper />}
                  />
                </Route>
                <Route
                  path="/dashboard/jobs/:jobId"
                  element={
                    <PreferencesSetRoute
                      element={
                        <Suspense fallback={<Loading />}>
                          <Dashboard />
                          <ShortlistJob />
                        </Suspense>
                      }
                      fallback={<JobsRedirect />}
                    />
                  }
                />

                <Route
                  path="/messaging/*"
                  element={
                    <PreferencesSetRoute
                      element={
                        <Suspense fallback={<Loading />}>
                          <Messaging />
                        </Suspense>
                      }
                    />
                  }
                />

                <Route
                  path="/login"
                  element={
                    <SignedOutRoute>
                      <Login />
                    </SignedOutRoute>
                  }
                />

                <Route
                  path="/forgot-password"
                  element={
                    <SignedOutRoute>
                      <ForgotPassword />
                    </SignedOutRoute>
                  }
                />
                <Route
                  path="/delete-my-account"
                  element={
                    <LoggedInRoute>
                      <DeleteMyAccount />
                    </LoggedInRoute>
                  }
                />
                <Route
                  path="/logout"
                  element={
                    <LoggedInRoute>
                      <Logout />
                    </LoggedInRoute>
                  }
                />
                <Route path="/jobs/*" element={<Jobs />} />
                <Route
                  path="/companies/*"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Companies />
                    </Suspense>
                  }
                />
                <Route
                  path="/salaries/*"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Salaries />
                    </Suspense>
                  }
                />
                <Route
                  path="/profile/*"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Profile />
                    </Suspense>
                  }
                />
                <Route path="/callback/*" element={<Callback />} />
                <Route
                  path="/account-settings/*"
                  element={<AccountSettings />}
                />

                <Route
                  path="/auth/oauth2/:method/callback"
                  element={<OAuthCallbacks />}
                />
                <Route
                  path="/write-an-endorsement/:surveyExternalId/*"
                  element={<EmployeeSurvey />}
                />
                <Route
                  path="/initial-preferences/*"
                  element={
                    <PreferencesRoute>
                      <Suspense fallback={<Loading />}>
                        <InitialPreferences />
                      </Suspense>
                    </PreferencesRoute>
                  }
                />
                <Route
                  path="/preferences/*"
                  element={
                    <PreferencesRoute>
                      <Suspense fallback={<Loading />}>
                        <JobPreferences />
                      </Suspense>
                    </PreferencesRoute>
                  }
                />
                <Route
                  path="refer"
                  element={
                    <LoggedInRoute>
                      <Referral />
                    </LoggedInRoute>
                  }
                />
                <Route
                  path="/onboarding/*"
                  element={
                    <PreferencesRoute>
                      <Suspense fallback={<Loading />}>
                        <Onboarding />
                      </Suspense>
                    </PreferencesRoute>
                  }
                />
                <Route path="*" element={<Navigate replace to="/" />} />
              </Route>
            </Routes>
          </PageWrapper>
          <Ottacue />
          <Toast />
        </>
      )}
    </>
  );
}
